import type { GenericField, GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import z from 'zod'
import type { RFileUploadResponse, FileMimeType } from 'v2/types/api/auth'
import { v4 } from 'uuid'
import { useAccountNumbersQuery } from 'v2/pages/settings/phone-numbers/queries'
import { ConvChannels } from 'v2/types/api/conversations'
import { type AiAgentMessageNodeData, FlowNodeTypes, type SidebarComponentProps } from '../types'
import { v2validations } from '../config/validation.config'
import { GenericSidebarComponent } from './sidebar.base'
import { aiAgentsFields } from './ai-agents/ai-agents-fields'
import { NodevarList } from '../components/nodevar-list'
import { MediaUpload } from '../components/media-upload/media-upload'

const messageSpecificFields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericField<AiAgentMessageNodeData['config']>[] => [
  {
    name: 'from',
    label: 'Send message from',
    zodValidation: (values) => {
      if (values.conversation_type === 'preset_message') {
        return z.string().min(1, 'From is required')
      }
      return z.string().optional()
    },
    componentType: 'input',
    visible: (values) => values.conversation_type === 'preset_message',
    onChangeListenTo: ['conversation_type'],
    inputProps: {
      endIcon: (
        <NodevarList
          nodevars={fieldsWithDynamicNodeVars.from}
          className="pr-2"
          inputElementSelector="[name=from]"
          replaceEntireField
        />
      ),
    },
  },
  {
    name: 'to',
    label: 'To',
    zodValidation: (values) => {
      if (values.conversation_type === 'preset_message') {
        return z.string().min(1, 'To is required')
      }
      return z.string().optional()
    },
    componentType: 'input',
    visible: (values) => values.conversation_type === 'preset_message',
    onChangeListenTo: ['conversation_type'],
    inputProps: {
      endIcon: (
        <NodevarList
          nodevars={nodeVars}
          className="pr-2"
          inputElementSelector="[name=to]"
          replaceEntireField
        />
      ),
    },
  },
  {
    name: 'text',
    label: 'Text',
    zodValidation: (values) => {
      if (values.conversation_type === 'preset_message') {
        return z.string().min(1, 'Text is required')
      }
      return z.string().optional()
    },
    componentType: 'textarea',
    visible: (values) => values.conversation_type === 'preset_message',
    onChangeListenTo: ['conversation_type'],
    inputProps: {
      placeholder: 'Enter text here',
      endIcon: (
        <NodevarList nodevars={nodeVars} className="pr-2" inputElementSelector="[name=text]" />
      ),
    },
  },
  {
    name: 'message_media',
    type: 'custom',
    label: 'Upload media (optional)',
    zodValidation: z.any(),
    visible: (values) => values.conversation_type === 'preset_message',
    onChangeListenTo: ['conversation_type'],
    eventDataExtractor: (data: Record<string, unknown>) => data,
    component: ({
      form,
      onChange,
    }: {
      form: GenericFormType<AiAgentMessageNodeData['config']>
      onChange: (value: Record<string, unknown>[]) => void
    }) => {
      const onUrlChange = (uploadedFile: RFileUploadResponse) => {
        const mediaFiles = form.state.values.message_media || []
        const newMediaFile = {
          media_type: uploadedFile.file_type,
          name: uploadedFile.file_name,
          url: uploadedFile.file_url,
          size: uploadedFile.file_size,
          file_id: uploadedFile.file_id,
        }
        onChange([...mediaFiles, newMediaFile])
      }

      const onRemove = (fileToRemove: RFileUploadResponse) => {
        const mediaFiles = form.state.values.message_media || []
        const updatedMediaFiles = mediaFiles.filter((file) => file.file_id !== fileToRemove.file_id)
        onChange(updatedMediaFiles)
      }

      // Convert message_media to RFileUploadResponse format for MediaUpload
      let initialFiles: RFileUploadResponse[] = []
      if (form.state.values.message_media) {
        initialFiles = form.state.values.message_media.map((file) => {
          if (!file.file_id) {
            file.file_id = v4()
          }
          return {
            file_type: file.media_type as FileMimeType,
            file_name: file.name || '',
            file_url: file.url || '',
            file_size: file.size || 0,
            file_id: file.file_id,
          }
        })
      }

      return (
        <MediaUpload
          onURLChange={onUrlChange}
          onRemove={onRemove}
          label="Upload media (optional)"
          initialFiles={initialFiles}
          multiUpload
          nodeVars={nodeVars}
        />
      )
    },
  },
]

const fields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericFieldConfig<AiAgentMessageNodeData['config']> => {
  const validations = v2validations({ nodeVars }).ai_agent_message
  const baseFields = aiAgentsFields(validations, FlowNodeTypes.ai_agent_message, nodeVars)
  const messageFields = messageSpecificFields(nodeVars, fieldsWithDynamicNodeVars)

  // Separate response-related fields from other base fields to control their position in the form
  const commonFields = baseFields.filter(
    (field) => field.name !== 'wait_for_response' && field.name !== 'reply_timeout',
  )
  const responseFields = baseFields.filter(
    (field) => field.name === 'wait_for_response' || field.name === 'reply_timeout',
  )

  // Return fields in the desired order: common fields first, then message-specific fields,
  // and response-related fields at the end for better UX and logical grouping
  return [...commonFields, ...messageFields, ...responseFields] as GenericFieldConfig<
    AiAgentMessageNodeData['config']
  >
}

// NODE_GEN: Update this code, do not commit directly
// Remove this comment after updating the code

export const AiAgentMessageSidebarComponent = (
  props: SidebarComponentProps<AiAgentMessageNodeData>,
) => {
  const { nodeVars } = props
  const { data: accountNumbers } = useAccountNumbersQuery('simple', ConvChannels.MESSAGE)
  const senderIds = accountNumbers?.map((number) => number.number)
  const fieldsWithDynamicNodeVars = {
    from: [...(senderIds?.flat() || []), ...nodeVars],
  }

  // Default from value is the Start.message.to if it exists, else the first sender id
  const defaultFrom = nodeVars.includes('{{Start.message.to}}')
    ? '{{Start.message.to}}'
    : fieldsWithDynamicNodeVars.from[0]

  // If `{{Start.message.from}}` exists in nodeVars, use it as default to value, else empty string
  const defaultTo = nodeVars.includes('{{Start.message.from}}') ? '{{Start.message.from}}' : ''

  const formFields = fields(nodeVars, fieldsWithDynamicNodeVars)
  const { selectedNode } = props

  return (
    <GenericSidebarComponent
      fields={formFields}
      {...props}
      className="relative"
      defaultValues={{
        from: selectedNode.data.config.from || defaultFrom,
        to: selectedNode.data.config.to || defaultTo,
      }}
    />
  )
}
